import React, { useState, useEffect } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@heroui/react";
import {
  AttachmentDto,
  DealComponentPaymentRequestDto,
  MarketplaceCardResponseDto,
  ProfileId,
  StepDefinition,
} from "../../../types/types";

import {
  getAliasedValue,
  getAliasKeys,
  getValueAtPath,
  setPathWithAliases,
  getPaymentType,
} from "../../../utils/deals/dealFormValueUtils";

import ComponentPaymentsStep from "./componentPaymentsStep";
import { useComponentPayments } from "./hooks/useComponentPayments";
import { useDealTranslation } from "./hooks/useDealTranslation";
import { useOptionsBySource } from "./hooks/useOptionsBySource";
import FinalReviewStep from "./finalReviewStep";
import {
  applyOutcomeRegistryDefaults,
  getOutcomeRegistryRows,
} from "../../../utils/opportunities/outcomeRegistryHelpers";

import {
  EVENT_DATE_FIELD_KEY,
  findEventDateField,
  getEventDate,
  getVisibleSteps,
  resolveStepId,
  isPayoutPoliciesStep,
  isNotesStep,
} from "../../../utils/deals/dealFormStepHelpers";
import {
  COMPONENT_PAYMENTS_STEP_ID,
  FINAL_REVIEW_STEP_ID,
  TemplateStep,
  SavedComponentPayment,
  FormValue,
} from "./dealFormTypes";
import { getSavedPaymentDates } from "../../../utils/deals/savedPaymentDates";
import { buildPayoutSummary } from "../../../utils/deals/dealPayoutSummary";
import { RegularStep } from "./steps/regularStep";
import { RepeatableStep } from "./steps/repeatableStep";
import { NotesStep } from "./steps/notesStep";
const DealForm: React.FC<{
  isGroupChat: boolean;
  availableFighters: Array<{
    id: ProfileId;
    firstName?: string;
    lastName?: string;
  }>;
  selectedFighterId: ProfileId | null;
  setSelectedFighterId: React.Dispatch<React.SetStateAction<ProfileId | null>>;
  errors: Record<string, string | undefined>;
  setErrors: React.Dispatch<
    React.SetStateAction<Record<string, string | undefined>>
  >;
  editedCard: MarketplaceCardResponseDto | null;
  setEditedCard: React.Dispatch<
    React.SetStateAction<MarketplaceCardResponseDto | null>
  >;
  loading: boolean;
  selectedFiles: File[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  existingAttachments: AttachmentDto[];
  attachmentsToRemove: string[];
  setAttachmentsToRemove: React.Dispatch<React.SetStateAction<string[]>>;
  uploadingFiles: boolean;
  isDragging: boolean;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  dragCounter: number;
  setDragCounter: React.Dispatch<React.SetStateAction<number>>;
  editMode: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onSubmit?: (
    payments?: Array<{
      paymentTiming: "pre_event" | "post_event";
      dueOn: string;
    }>,
    signatureDataUrl?: string,
  ) => void;
  onPreviewAgreement?: (payments?: DealComponentPaymentRequestDto[]) => Promise<boolean>;
  agreementPdfUrl?: string | null;
  currentStep: number;
  canProceed: boolean;
  templateSteps?: StepDefinition[];
  lockEventOpportunityFields?: boolean;
  savedComponentPayments?: SavedComponentPayment[];
  requiresSignature: boolean;
}> = ({
  isGroupChat,
  availableFighters,
  selectedFighterId,
  setSelectedFighterId,
  errors,
  setErrors,
  editedCard,
  setEditedCard,
  loading,
  selectedFiles,
  setSelectedFiles,
  existingAttachments,
  attachmentsToRemove,
  setAttachmentsToRemove,
  uploadingFiles,
  isDragging,
  setIsDragging,
  dragCounter,
  setDragCounter,
  editMode,
  onNext,
  onPrevious,
  onSubmit,
  currentStep,
  canProceed,
  templateSteps,
  lockEventOpportunityFields = false,
  savedComponentPayments,
  requiresSignature,
  onPreviewAgreement,
  agreementPdfUrl
}) => {
  const { t, tr } = useDealTranslation();

  const getRawTemplateSteps = (): TemplateStep[] => {
    const fromCard = editedCard?.opportunityCardResponse?.templateJson?.steps;
    if (Array.isArray(fromCard) && fromCard.length > 0) return fromCard;
    if (Array.isArray(templateSteps) && templateSteps.length > 0)
      return templateSteps;
    return [];
  };
  const rawTemplateSteps: TemplateStep[] = getRawTemplateSteps();

  const { optionsBySource, loadingOptionsBySource } = useOptionsBySource(
    rawTemplateSteps,
  );


  const steps: StepDefinition[] = [...getVisibleSteps(rawTemplateSteps), {
    stepId: COMPONENT_PAYMENTS_STEP_ID,
    title: t("componentPaymentsStepTitle"),
    description: t("componentPaymentsStepDescription"),
  }];
  if (requiresSignature) {
    steps.push({
      stepId: FINAL_REVIEW_STEP_ID,
      title: t("finalReviewStepTitle"),
    });
  }


  const formData = editedCard?.opportunityCardResponse?.formData ?? {};

  const [signature, setSignature] = useState<string | null>(null);
  const savedDates = getSavedPaymentDates(savedComponentPayments, editedCard);
  const paymentType = getPaymentType(formData);

  const eventDateField = findEventDateField(rawTemplateSteps);
  const eventDateErrorKey = eventDateField
    ? `${eventDateField.stepId}.${EVENT_DATE_FIELD_KEY}`
    : null;
  const eventDate = getEventDate(formData, rawTemplateSteps);
  const minDaysForEventDate: number =
    eventDateField?.field.validation?.minDaysFromNow ?? 0;

  const getIsEventDateInvalid = (): boolean => {
    if (!eventDate || !minDaysForEventDate) return false;
    // The form data is the edited copy, so in edit mode the date always equals
    // the "original" one and is never re-checked here; the backend still checks it.
    if (editMode) return false;
    const min = new Date();
    min.setHours(0, 0, 0, 0);
    min.setDate(min.getDate() + minDaysForEventDate);
    const eventDateObj = new Date(eventDate);
    eventDateObj.setHours(0, 0, 0, 0);
    return eventDateObj < min;
  };
  const isEventDateInvalid: boolean = getIsEventDateInvalid();

  useEffect(() => {
    if (!eventDateErrorKey) return;
    setErrors((prev) => {
      const key = eventDateErrorKey;
      const next = isEventDateInvalid
        ? t("eventDateNoLongerValid")
        : undefined;
      if (prev[key] === next) return prev;
      return { ...prev, [key]: next };
    });
  }, [isEventDateInvalid, eventDateErrorKey, t]);

  const updateFormAtPath = (path: string[], value: FormValue) => {
    const leafKey = path[path.length - 1];
    const dotKey = path.join(".");

    setEditedCard((prev) => {
      if (!prev) return prev;
      const prevForm = prev.opportunityCardResponse?.formData ?? {};
      const next = setPathWithAliases(prevForm, path, value);

      const oldVal = getValueAtPath(prevForm, path);
      if (oldVal === value) return prev; 

      let nextWithPolicyCleanup = next;
      if (leafKey === "componentType") {
        rawTemplateSteps.forEach((step) => {
          if (!step?.stepId || !step?.outcomeRegistry) return;
          const stepId = step.stepId;

          getOutcomeRegistryRows(step.outcomeRegistry).forEach((row) => {
            nextWithPolicyCleanup = setPathWithAliases(
              nextWithPolicyCleanup,
              [stepId, "outcomes", row.key, "selectedComponents"],
              [],
            );
          });
        });
      }

      const withDefaults = applyOutcomeRegistryDefaults(
        nextWithPolicyCleanup,
        rawTemplateSteps,
      );

      return {
        ...prev,
        opportunityCardResponse: {
          ...prev.opportunityCardResponse,
          formData: withDefaults,
        },
      } as MarketplaceCardResponseDto;
    });

    const currentRenderValue = getValueAtPath(formData, path);
    if (currentRenderValue === value) return;

    setErrors((prevErr) => {
      const aliasKeys = [leafKey, ...getAliasKeys(leafKey)];
      const hasAny =
        aliasKeys.some((k) => prevErr[k] !== undefined) ||
        prevErr[dotKey] !== undefined;
      if (!hasAny) return prevErr;
      const nextErr = { ...prevErr, [dotKey]: undefined };
      for (const k of aliasKeys) nextErr[k] = undefined;
      return nextErr;
    });
  };

  const getFormValue = (stepId: string, key: string): FormValue => {
    const sd = formData[stepId];
    if (sd == null) return undefined;
    return getAliasedValue(sd, key);
  };

  const updateFieldValue = (stepId: string, key: string, value: FormValue) => {
    updateFormAtPath(
      key.includes(".") ? [stepId, ...key.split(".")] : [stepId, key],
      value,
    );
  };

  const isLockedEventOpportunityField = (
    stepId: string,
    fieldKey: string,
  ): boolean => {
    if (!lockEventOpportunityFields) return false;

    if (stepId !== eventDateField?.stepId) return false;

    const normalizedFieldKey = String(fieldKey || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

    return [
      "eventdate",
      "country",
      "city",
      "venue",
      "sport",
      "combatsport",
      "weightclass",
      "locationcountry",
      "locationcity",
      "locationvenue",
    ].includes(normalizedFieldKey);
  };

  const fieldHelpers = {
    getFormValue,
    updateFieldValue,
    errors,
    formData,
    availableFighters,
    optionsBySource,
    loadingOptionsBySource,
    isLockedField: isLockedEventOpportunityField,
    tr,
  };

  const payoutSummary = buildPayoutSummary(formData, optionsBySource, rawTemplateSteps, t);

  const componentPayments = useComponentPayments({
    formData,
    templateSteps: rawTemplateSteps,
    savedDates,
    payoutSummary,
    paymentType,
    t,
  });
  const { hasErrors: hasDateErrors } = componentPayments;

  const renderComponentPaymentsStep = (): React.ReactNode => (
    <ComponentPaymentsStep
      payoutSummary={payoutSummary}
      preMatchPayouts={componentPayments.preMatchPayouts}
      postMatchPayouts={componentPayments.postMatchPayouts}
      preMatchDate={componentPayments.dates.pre}
      setPreMatchDate={componentPayments.setPreDate}
      postMatchDate={componentPayments.dates.post}
      setPostMatchDate={componentPayments.setPostDate}
      formData={formData}
      dateWindows={componentPayments.windows}
      deadlineOffsets={componentPayments.offsets}
      dateErrors={componentPayments.errors}
    />
  );

  const renderStep = (step: StepDefinition): React.ReactNode => {
    if (step.stepId === COMPONENT_PAYMENTS_STEP_ID)
      return renderComponentPaymentsStep();
    if (step.stepId === FINAL_REVIEW_STEP_ID)
      return (
        <FinalReviewStep
          pdfUrl={agreementPdfUrl}
          signature={signature}
          onSignatureChange={setSignature}
          showPdf={true}
        />
      );
    const stepId = resolveStepId(step);
    if (step.repeatable === true)
      return (
        <RepeatableStep
          step={step}
          stepId={stepId}
          formData={formData}
          fieldHelpers={fieldHelpers}
          t={t}
          updateFormAtPath={updateFormAtPath}
        />
      );
    if (isNotesStep(step))
      return (
        <NotesStep
          step={step}
          stepId={stepId}
          formData={formData}
          fieldHelpers={fieldHelpers}
          t={t}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          existingAttachments={existingAttachments}
          attachmentsToRemove={attachmentsToRemove}
          setAttachmentsToRemove={setAttachmentsToRemove}
          uploadingFiles={uploadingFiles}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
          dragCounter={dragCounter}
          setDragCounter={setDragCounter}
        />
      );
    return (
      <RegularStep
        step={step}
        stepId={stepId}
        formData={formData}
        fieldHelpers={fieldHelpers}
        t={t}
        updateFormAtPath={updateFormAtPath}
        isGroupChat={isGroupChat}
        isFirstStep={step === steps[0]}
        availableFighters={availableFighters}
        selectedFighterId={selectedFighterId}
        setSelectedFighterId={setSelectedFighterId}
        errors={errors}
        setErrors={setErrors}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        existingAttachments={existingAttachments}
        attachmentsToRemove={attachmentsToRemove}
        setAttachmentsToRemove={setAttachmentsToRemove}
        uploadingFiles={uploadingFiles}
        isDragging={isDragging}
        setIsDragging={setIsDragging}
        dragCounter={dragCounter}
        setDragCounter={setDragCounter}
        rawTemplateSteps={rawTemplateSteps}
      />
    );
  };


  const currentStepDef = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  // In create mode the first step goes back to the opportunity list.
  const showPrevious = currentStep > 0 || !editMode;
  const isPaymentStep = currentStepDef?.stepId === COMPONENT_PAYMENTS_STEP_ID;
  const isFinalReviewActive = currentStepDef?.stepId === FINAL_REVIEW_STEP_ID;
  const currentStepId = currentStepDef ? resolveStepId(currentStepDef) : "";
  const isCurrentStepPayoutPolicies = currentStepDef
    ? isPayoutPoliciesStep(currentStepDef)
    : false;
  const payoutPolicyError = isCurrentStepPayoutPolicies
    ? errors[`${currentStepId}.components`]
    : undefined;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-2 overflow-x-auto px-4 sm:px-8 pb-3 border-b border-gray-100 shrink-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {steps.map((s, index) => {
          const active = index === currentStep;
          const done = index < currentStep;
          const key = s.stepId ?? s.id ?? String(index);
          return (
            <span
              key={key}
              className={[
                  "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap shrink-0 transition-all duration-200",
                  active
                    ? "bg-[#D9EBFF] text-[#0080FF] border-transparent"
                    : done
                      ? "bg-[#DCFCE7] text-[#16A34A] border-transparent"
                      : "bg-white text-[#71717A] border-[#E4E4E7]",
                ].join(" ")}
              >
                {done ? (
                  <span className="w-4 h-4 rounded-full flex items-center justify-center bg-[#16A34A] text-white">
                    <CheckIcon className="w-2.5 h-2.5" />
                  </span>
                ) : (
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    active ? "bg-[#0080FF] text-white" : "bg-[#D9EBFF] text-[#0080FF]"
                  }`}>
                    {index + 1}
                  </span>
                )}
                {tr(s.title ?? s.label ?? s.stepId ?? "")}
              </span>
            );
          })}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-8 py-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
      {currentStepDef && (
        <div className="flex items-start gap-4 mb-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h2 className="text-base font-bold text-[#11181C] leading-tight font-['Plus_Jakarta_Sans']">
                  {tr(
                    currentStepDef.title ??
                      currentStepDef.label ??
                      currentStepDef.stepId ??
                      "",
                  )}
                </h2>
                {payoutPolicyError && (
                  <p className="text-[11px] font-semibold leading-4 text-red-500">
                    {payoutPolicyError}
                  </p>
                )}
              </div>

              {currentStepDef.description && (
                <p className="text-[11px] text-[#71717A] mt-0.5 font-['Plus_Jakarta_Sans']">
                  {tr(currentStepDef.description)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div>{currentStepDef ? renderStep(currentStepDef) : null}</div>
      </div>

      <div className="flex items-center gap-2 sm:justify-end px-4 sm:px-8 py-4 border-t border-gray-100 bg-white shrink-0">
        {showPrevious && (
          <Button
            variant="flat"
            radius="full"
            onPress={() => {
              if (isFinalReviewActive) setSignature(null);
              onPrevious();
            }}
            className="shrink-0 h-9 px-5 text-sm font-medium text-[#11181C] bg-[#EBEBEC] hover:bg-[#E0E0E1]"
            startContent={<ArrowLeftIcon className="w-4 h-4" />}
          >
            {t("previous")}
          </Button>
        )}

        {!isLastStep ? (
          <Button
            color="primary"
            radius="full"
            isDisabled={!canProceed || loading || (isPaymentStep && hasDateErrors)}
            isLoading={loading}
            onPress={async () => {
              if (isPaymentStep) {
                if (hasDateErrors) return;
                if (steps[currentStep + 1]?.stepId === FINAL_REVIEW_STEP_ID) {
                  setSignature(null);
                  const ok = await onPreviewAgreement?.(componentPayments.buildPayments());
                  if (!ok) return;
                }
              }
              onNext();
            }}
            className="flex-1 sm:flex-none h-9 px-5 text-sm font-medium text-[#0080FF] bg-[#EBEBEC] hover:bg-[#E0E0E1]"
            endContent={<ArrowRightIcon className="w-4 h-4" />}
          >
            {t("next")}
          </Button>
        ) : (
          <Button
            color="primary"
            radius="full"
            isDisabled={
              !canProceed || loading || hasDateErrors || (isFinalReviewActive && !signature)
            }
            isLoading={loading}
            onPress={() => {
              if (loading) return;
              if (hasDateErrors) return;
              if (isFinalReviewActive) {
                onSubmit?.(componentPayments.buildPayments(), signature ?? undefined);
                return;
              }
              if (onSubmit) onSubmit(componentPayments.buildPayments());
            }}
            className="flex-1 sm:flex-none h-10 px-5 text-sm font-bold text-white bg-[#0080FF] hover:bg-blue-600"
            startContent={<CheckIcon className="w-4 h-4" />}
          >
            {isFinalReviewActive ? t("signAndMakeADeal") : t(editMode ? "updateDeal" : "makeADeal")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DealForm;
